import type { FormEvent, KeyboardEvent } from "react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import Button from "@/components/Button";
import Banner from "@/components/Banner";
import IconButton from "@/components/IconButton";
import TextField from "@/components/TextField";
import ChatRichContent from "./ChatRichContent";
import { MCP_PLAYGROUND_SUGGESTIONS } from "./constants";
import McpPlaygroundAuthBanner from "./McpPlaygroundAuthBanner";
import McpWriteConfirmationCard from "./McpWriteConfirmationCard";
import type { McpPlaygroundAuthStatus } from "./useMcpPlaygroundAuth";
import type { McpWriteProposal } from "./writeProposals";
import { stripWriteProposalMarker } from "./writeProposals";
import { messageText, type McpPlaygroundMessage } from "./chatMessages";
import { isSupportedMcpPlaygroundWriteTool } from "./access";
import { useMcpPlaygroundChatScroll } from "./useMcpPlaygroundChatScroll";

export type { McpPlaygroundMessage };
export { messageText };

function lastMessageIndex(messages: McpPlaygroundMessage[], role: string): number {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    if (messages[index]?.role === role) {
      return index;
    }
  }

  return -1;
}

type McpPlaygroundChatViewProps = {
  messages: McpPlaygroundMessage[];
  input: string;
  isLoading: boolean;
  error?: Error;
  authStatus: McpPlaygroundAuthStatus;
  pendingProposal?: McpWriteProposal | null;
  canConfirmWrite?: boolean;
  onInputChange: (value: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onConfirmWrite: (proposal: McpWriteProposal) => Promise<void>;
  onDismissWrite: (proposal: McpWriteProposal) => void;
};

export default function McpPlaygroundChatView({
  messages,
  input,
  isLoading,
  error,
  authStatus,
  pendingProposal,
  canConfirmWrite = false,
  onInputChange,
  onSubmit,
  onConfirmWrite,
  onDismissWrite
}: McpPlaygroundChatViewProps) {
  const formRef = useRef<HTMLFormElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const canSend = !isLoading && authStatus === "ready" && input.trim().length > 0;
  const inputDisabled = isLoading || authStatus !== "ready";
  const lastAssistantMessage = useMemo(
    () => [...messages].reverse().find((message) => message.role === "assistant"),
    [messages]
  );
  const lastAssistantContent = lastAssistantMessage ? messageText(lastAssistantMessage.content) : "";
  const { scrollRef, transcriptRef, latestAssistantMessageRef, latestUserMessageRef, thinkingStatusRef } =
    useMcpPlaygroundChatScroll({
      messages,
      isLoading,
      lastAssistantContent
    });

  const focusComposer = useCallback(() => {
    if (inputDisabled || pendingProposal) {
      return;
    }

    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  }, [inputDisabled, pendingProposal]);

  useEffect(() => {
    if (!isLoading && !pendingProposal) {
      focusComposer();
    }
  }, [focusComposer, isLoading, messages.length, pendingProposal]);

  const handleComposerKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    // @lit/react delivers a native KeyboardEvent; React's mock path uses a SyntheticEvent.
    const composing =
      event.nativeEvent?.isComposing === true || (event as unknown as globalThis.KeyboardEvent).isComposing === true;

    if (event.key !== "Enter" || event.shiftKey || composing) {
      return;
    }

    event.preventDefault();
    if (canSend) {
      formRef.current?.requestSubmit();
    }
  };

  return (
    <section className="flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-sys-brand-outline-variant bg-sys-brand-surface shadow-web-1">
      <header className="shrink-0 border-b border-sys-brand-outline-variant px-5 py-4">
        <h1 className="text-title-large text-sys-brand-on-surface" data-testid="nav-header">
          Chat with Member 360
        </h1>
        <p className="mt-1 text-body-small text-sys-brand-on-surface-variant">
          Ask questions, search members, and draft notes or interactions.
        </p>
      </header>

      <div ref={scrollRef} className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto scroll-smooth px-5 pb-10 pt-4">
        <div ref={transcriptRef} className="flex min-h-0 flex-1 flex-col gap-4">
          <McpPlaygroundAuthBanner status={authStatus} />

          {pendingProposal && canConfirmWrite ? (
            <McpWriteConfirmationCard
              proposal={pendingProposal}
              disabled={authStatus !== "ready"}
              onConfirm={onConfirmWrite}
              onDismiss={onDismissWrite}
            />
          ) : null}

          {pendingProposal && !canConfirmWrite ? (
            <Banner
              variant="warn"
              testId="mcp-write-permission-banner"
              description={
                isSupportedMcpPlaygroundWriteTool(pendingProposal.tool)
                  ? "This agent proposed a write, but you do not have permission to save notes or interactions from this page. You can still use read and search prompts, or ask an authorized colleague to save the change in Member 360."
                  : "This agent proposed an unsupported write action. Only staff notes and CRM interactions can be confirmed from Chat with Member 360."
              }
            />
          ) : null}

          {error ? (
            <div role="alert">
              <Banner variant="error" description={error.message} testId="mcp-playground-error" />
            </div>
          ) : null}

          <div
            className={
              messages.length === 0
                ? "flex flex-1 flex-col items-center justify-center gap-6 py-8 text-center"
                : "flex flex-col gap-4 pb-2"
            }
            data-testid="mcp-playground-messages"
            aria-live="polite"
            aria-atomic="false"
            aria-relevant="additions"
          >
            {messages.length === 0 ? (
              <>
                <div className="max-w-md space-y-2">
                  <p className="text-title-medium text-sys-brand-on-surface">What can I help you find?</p>
                  <p className="text-body-medium text-sys-brand-on-surface-variant">
                    Try a prompt below or type your own question.
                  </p>
                </div>
                <section className="flex max-w-2xl flex-wrap justify-center gap-2" aria-label="Suggested prompts">
                  {MCP_PLAYGROUND_SUGGESTIONS.map((suggestion) => (
                    <Button
                      key={suggestion}
                      variant="outlined"
                      disabled={isLoading}
                      onClick={() => onInputChange(suggestion)}
                    >
                      {suggestion}
                    </Button>
                  ))}
                </section>
                {isLoading ? (
                  <output
                    className="rounded-2xl border border-dashed border-sys-brand-outline-variant bg-sys-brand-surface-container-low px-4 py-3 text-body-medium text-sys-brand-on-surface-variant"
                    role="status"
                  >
                    Thinking…
                  </output>
                ) : null}
              </>
            ) : (
              <>
                {messages.map((message, index) => {
                  const text = stripWriteProposalMarker(messageText(message.content));
                  const isUser = message.role === "user";
                  const isLatestAssistant = !isUser && lastMessageIndex(messages, "assistant") === index;
                  const isLatestUser = isUser && lastMessageIndex(messages, "user") === index;

                  return (
                    <article
                      key={message.id}
                      ref={(element) => {
                        if (isLatestAssistant) {
                          latestAssistantMessageRef.current = element;
                        }
                        if (isLatestUser) {
                          latestUserMessageRef.current = element;
                        }
                      }}
                      aria-label={isUser ? "Your message" : "Agent message"}
                      className={
                        isUser
                          ? "self-end max-w-[min(85%,42rem)] rounded-2xl rounded-br-md bg-sys-brand-primary px-4 py-3 text-sys-brand-on-primary"
                          : "self-start max-w-[min(85%,42rem)] rounded-2xl rounded-bl-md border border-sys-brand-outline-variant bg-sys-brand-surface-container-low px-4 py-3 text-sys-brand-on-surface"
                      }
                      data-testid={`mcp-playground-message-${message.role}`}
                    >
                      {isUser ? (
                        <p className="whitespace-pre-wrap text-body-medium">{text}</p>
                      ) : (
                        <ChatRichContent text={text} />
                      )}
                    </article>
                  );
                })}
                {isLoading ? (
                  <output
                    ref={thinkingStatusRef}
                    className="self-start max-w-[min(85%,42rem)] rounded-2xl rounded-bl-md border border-dashed border-sys-brand-outline-variant bg-sys-brand-surface-container-low px-4 py-3 text-body-medium text-sys-brand-on-surface-variant"
                    role="status"
                  >
                    Thinking…
                  </output>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>

      <form
        ref={formRef}
        className="flex shrink-0 items-end gap-2 border-t border-sys-brand-outline-variant px-5 py-4"
        onSubmit={onSubmit}
      >
        <TextField
          ref={inputRef}
          className="min-w-0 flex-1 [&_md-outlined-text-field]:w-full"
          disabled={inputDisabled}
          placeholder="Ask Member 360 anything…"
          rows={2}
          type="textarea"
          value={input}
          onChangeText={onInputChange}
          onKeyDown={handleComposerKeyDown}
        />
        <IconButton
          name="send"
          ariaLabel={isLoading ? "Sending message" : "Send message"}
          title={isLoading ? "Sending message" : "Send message"}
          disabled={!canSend}
          skipMargin
          variant={canSend ? "filled" : "filledTonal"}
          data-testid="mcp-playground-send"
          containerClassName="shrink-0"
          onClick={() => {
            if (canSend) {
              formRef.current?.requestSubmit();
            }
          }}
        />
      </form>
    </section>
  );
}
